import React from 'react';
import {
  TextInput,
  ReferenceInput,
  SelectInput,
  BooleanInput,
  FileInput,
  FileField,
  ImageInput,
  ImageField,
  FormDataConsumer,
  required,
  useGetList,
  useRecordContext
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { AcademicClassGroupInput } from './classGroups';

/**
 * The fields shared by Create and Edit.
 *
 * Kept in one file because the two forms must stay identical: they write the
 * same row, and a field present on one but not the other silently drops that
 * column on save.
 */

/**
 * Subject drives the chapter list, so changing it has to clear whatever chapter
 * was picked. Without this the form can submit a chapter belonging to the old
 * subject, which the API rejects with "The selected chapter does not belong to
 * the selected subject".
 */
const SubjectSelectInput = (props: any) => {
  const { setValue } = useFormContext();

  return (
    <SelectInput
      {...props}
      optionText="subject"
      validate={[required()]}
      className="two_three_input"
      onChange={() => {
        setValue('chapter_id', null);
      }}
    />
  );
};

/**
 * Show the existing file on the edit form.
 *
 * react-admin's FileInput and ImageInput render nothing for a value that is a
 * plain URL string rather than one of their own file objects, so without this
 * an admin opening an existing record sees an empty upload box and cannot tell
 * whether a file is attached.
 *
 * An image is shown rather than linked. The whole point of allowing image
 * content is that it can be judged at a glance, and a link defeats that.
 */
const ExistingFileDisplay = () => {
  const record = useRecordContext();
  if (!record || !record.file_url) return null;

  const isImage = record.file_type === 'image';

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Current file</div>

      {isImage ? (
        <a href={record.file_url} target="_blank" rel="noopener noreferrer">
          <img
            src={record.file_url}
            alt={record.title}
            style={{ maxWidth: 220, maxHeight: 160, borderRadius: 4, display: 'block' }}
          />
        </a>
      ) : (
        <a href={record.file_url} target="_blank" rel="noopener noreferrer">
          Open the uploaded PDF
        </a>
      )}

      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
        Choosing a new file below replaces it.
      </div>
    </div>
  );
};

/**
 * The same problem for the thumbnail, which every content type can have --
 * video included, where it is the only picture the card will ever show.
 */
const ExistingThumbnailDisplay = () => {
  const record = useRecordContext();
  if (!record || !record.thumbnail_url) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Current thumbnail</div>
      <img
        src={record.thumbnail_url}
        alt=""
        style={{ maxWidth: 180, maxHeight: 120, borderRadius: 4, display: 'block' }}
      />
      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
        Choosing a new image below replaces it.
      </div>
    </div>
  );
};

export const AcademicContentFormFields = () => {
  // Loaded once so the form can read `media` off the chosen content type and
  // decide between the PDF upload and the video URL field. Five rows, so the
  // page size is generous on purpose.
  const { data: contentTypes } = useGetList('content-type', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'display_order', order: 'ASC' }
  });

  const mediaFor = (contentTypeId: any): string => {
    if (!contentTypeId || !contentTypes) return 'pdf';
    const match = contentTypes.find((t: any) => String(t.id) === String(contentTypeId));
    return match && match.media ? match.media : 'pdf';
  };

  return (
    <>
      <TextInput
        source="title"
        label="Title"
        fullWidth={true}
        validate={[required()]}
        helperText="What students see on the card, e.g. 'Rotational Motion — Full Notes'"
      />

      <TextInput
        source="description"
        label="Description"
        fullWidth={true}
        multiline
        rows={2}
      />

      <ReferenceInput source="exam_id" reference="exam" fullWidth={true}>
        <SelectInput
          optionText="exam_name"
          validate={[required()]}
          className="one_three_input"
        />
      </ReferenceInput>

      {/* Only 10th/11th/12th/Dropper -- see classGroups.tsx. */}
      <AcademicClassGroupInput
        source="class_group_id"
        fullWidth={true}
        validate={[required()]}
        className="two_three_input"
      />

      <ReferenceInput source="subject_id" reference="subject" fullWidth={true}>
        <SubjectSelectInput />
      </ReferenceInput>

      {/* Chapter is optional and deliberately so: most material covers a whole
          subject. Leaving it blank files the item under the subject. */}
      <FormDataConsumer>
        {({ formData }) => (
          <ReferenceInput
            source="chapter_id"
            reference="chapter"
            perPage={500}
            sort={{ field: 'chapter_name', order: 'ASC' }}
            filter={formData.subject_id ? { 'subject_id=': formData.subject_id } : {}}
            fullWidth={true}
          >
            <SelectInput
              className="one_three_input"
              optionText="chapter_name"
              label="Chapter (optional)"
              disabled={!formData.subject_id}
              helperText={
                formData.subject_id
                  ? 'Leave blank if this covers the whole subject'
                  : 'Pick a subject first'
              }
            />
          </ReferenceInput>
        )}
      </FormDataConsumer>

      <ReferenceInput
        source="content_type_id"
        reference="content-type"
        perPage={50}
        fullWidth={true}
      >
        <SelectInput
          optionText="content_type"
          validate={[required()]}
          className="two_three_input"
        />
      </ReferenceInput>

      {/* One of these two, never both. Which one is decided by the content
          type's `media`, and the API clears the other on save.

          The upload branch takes a PDF OR an image. Which one it turned out to
          be is recorded per item on file_type, derived server-side from the
          file -- the content type does not decide it, so a type can hold both.
          See schema/migrations/master/0011_academic_content_media.sql. */}
      <FormDataConsumer>
        {({ formData }) =>
          mediaFor(formData.content_type_id) === 'video' ? (
            <TextInput
              source="video_url"
              label="Video URL"
              fullWidth={true}
              validate={[required()]}
              helperText="Link to the video, e.g. a YouTube or Vimeo URL"
            />
          ) : (
            <>
              <ExistingFileDisplay />
              <FileInput
                source="file_url"
                label="PDF or image"
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/gif': ['.gif'],
                  'image/webp': ['.webp']
                }}
                maxSize={31457280}
                placeholder={<p>Drop a PDF or an image here, or click to choose one. 30 MB maximum.</p>}
                helperText="An image is one page. Anything with several pages belongs in a PDF, so students get it in one item rather than as page-1, page-2."
              >
                <FileField source="src" title="title" />
              </FileInput>
            </>
          )
        }
      </FormDataConsumer>

      {/* Outside the branch above on purpose: every content type can have a
          thumbnail, and a video needs one most -- there is no page to preview,
          so without this its card shows nothing but an icon.

          Capped well below the server's 30 MB. That limit is sized for a
          textbook PDF; a card preview that big would be a mistake, and
          rejecting it here explains itself better than a slow failed upload. */}
      <ExistingThumbnailDisplay />
      <ImageInput
        source="thumbnail_url"
        label="Thumbnail (optional)"
        accept={{
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/gif': ['.gif'],
          'image/webp': ['.webp']
        }}
        maxSize={2097152}
        placeholder={<p>Drop a cover image here, or click to choose one. 2 MB maximum.</p>}
        helperText="Shown on the card in the student apps. Leave blank to use the content-type icon."
      >
        <ImageField source="src" title="title" />
      </ImageInput>

      {/* Unpublish without deleting: the row and its file both survive, but
          students stop seeing it. */}
      <BooleanInput source="is_active" label="Published" defaultValue={true} />
    </>
  );
};
